import { Injectable, inject } from '@angular/core';
import { AssetDto, AssetVersionDto } from '@picflow/types';
import { captureMessage } from '@sentry/angular-ivy';
import { Observable, filter, of, switchMap, take, throwError } from 'rxjs';
import { FileRemovedDialogComponent } from '../gallery/dialogs/file-removed-dialog/file-removed-dialog.component';
import { GROWTHBOOK } from '../growthbook';
import { AssetService } from './asset.service';
import { DialogService } from './dialog.service';

@Injectable({
  providedIn: 'root',
})
export class VirusScanService {
  private growthBook = inject(GROWTHBOOK);
  private dialogs = inject(DialogService);
  private removed = new Set<string>();
  private assetService = inject(AssetService);

  private async removeInfectedAsset(asset: AssetDto) {
    if (this.removed.has(asset.id)) return;
    this.removed.add(asset.id);
    this.dialogs.open(FileRemovedDialogComponent, { asset });
    captureMessage('file_removed_due_to_virus_scan');
    await this.assetService.deleteAsset(asset);
  }

  public checkAsset(asset: AssetDto) {
    if (asset.version_data.scan_status === 'infected') {
      this.removeInfectedAsset(asset);
    }
  }

  public onScanned(version: AssetVersionDto): Observable<AssetVersionDto> {
    if (!this.growthBook.isOn('virus-scan')) return of(version); // feature flag
    if (version.scan_status === 'no') return of(version); // legacy file from before virus scanning was implemented
    if (version.scan_status === 'clean') return of(version);
    if (version.scan_status === 'infected') return throwError(() => 'infected');

    const timeSinceUpload = Date.now() - version.created_at * 1000;

    if (timeSinceUpload > 1000 * 60 * 1) {
      captureMessage('scan_took_too_long');
      return of(version);
    }

    return this.assetService.updates.pipe(
      filter(update => update.asset?.id !== version.asset),
      take(1),
      switchMap(() => this.onScanned(version))
    );
  }
}
